import { TranslateService } from '@ngx-translate/core';
import { environment } from './../../environments/environment';
import { Inject, Injectable } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { Constants } from './../../../src/constants';
import { UserDto } from '../models/dto/user.dto';
import { ItemTreeType } from '../models/enum/item-tree.enum';
import { PermissionType } from '../models/enum/permission.enum';
import { HttpService } from './http.service';
import { HttpResponse } from '@angular/common/http';
import { FileName } from '../models/file-name';
import { ActivatedRoute, Params, Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class HelperService {

  constructor(
    @Inject(TranslateService) private translate: TranslateService,
    private http: HttpService,
  ) { }

  get isUserType() {
    return ItemTreeType.user;
  }

  get isOrganizationType() {
    return ItemTreeType.organization;
  }

  get isCustomerType() {
    return ItemTreeType.customer;
  }

  isNodeHasParent(data: any) {
    return data && Object.prototype.hasOwnProperty.call(data, 'currentNode') ? true : false;
  }

  isCustomerAdminPermission(profile: UserDto) {
    return this.findPermission(profile, [PermissionType.customerAdmin]);
  }

  isPortalAdminOrPortalUserPermission(profile: UserDto) {
    return this.findPermission(profile, [PermissionType.portalAdmin, PermissionType.portalUser])
  }

  findPermission(profile: UserDto, array: PermissionType[]): boolean {
    return profile?.permissions.some(el => array.includes(el.name));
  }

  searchByFields(paramName: string, terms: Observable<string>, service: any): Subscription {
    return terms.pipe(debounceTime(Constants.searchTimeOut), distinctUntilChanged())
      .subscribe(data => service.setQueryParameter(paramName, data));
  }

  setParamsAndUpdate(data: string[], params: string[], urlParameters: Params, router: Router, route: ActivatedRoute) {
    if (!urlParameters) return;
    data.forEach((el, index) => {
      const value = el != null ? el.trim() : null;
      const param = params[index];
      if (value) {
        urlParameters[param] = encodeURI(value)
      } else {
        delete urlParameters[param];
      }
    });
    router.navigate([], { relativeTo: route, queryParams: urlParameters });
  }

  getValueFromParams(queryParam: any, paramValue: string, subjectValue: Subject<string>) {
    const source = decodeURI(queryParam[paramValue] ?? '');
    subjectValue.next(source);
    return source;
  }

  onSortTable(event: { active: string, direction: string }, service: any) {
    if (event.direction == '') {
      event.active = 'created';
      event.direction = 'desc';
    }
    service.sort = {
      propertyName: event.active,
      isDescending: event.direction === 'desc'
    };
  }

  onPage(pageEvent: any, service: any) {
    service.page = pageEvent.pageIndex + 1;
    service.pageSize = pageEvent.pageSize;
  }

  getLangSelectObj() {
    const obj: any[] = [];
    const items = environment.locales;
    items.forEach(el => {
      const name = this.matchLangName(el);
      obj.push({ id: el, name: name });
    });
    return obj;
  }

  matchLangName(lang: string) {
    return this.translate.instant(`LANGUAGE.${lang}`);
  }

  getSupportedCultures(): { culture: string }[] {
    return environment.locales.map(locale => ({ culture: locale }));
  }

  getAvailableTimezones(): { timezone: string }[] {
    const supportedValuesOf = (Intl as any).supportedValuesOf;
    const zones: string[] = typeof supportedValuesOf === 'function'
      ? supportedValuesOf('timeZone')
      : [];
    return zones.map(zone => ({ timezone: zone }));
  }

  isMatchedToArrays(arr1: any[], arr2: any[]) {
    return arr1.length === arr2.length && arr1.every(el => arr2.includes(el));
  }

  static extractFilenameFromTitleHeader(titleHeader: string): string {
    const fileNameHeader = splitStringOnSemicolonsOutsideOfString(titleHeader)[1];

    const fileNameString = fileNameHeader
      .split('=')[1]
      .replace('\\', '')
      .replace(/"/g, '');

    return fileNameString;
  }

  getFileNameFromHeaders(resp: HttpResponse<Blob>): string {
    const titleHeader = resp.headers.get('content-disposition');
    return HelperService.extractFilenameFromTitleHeader(titleHeader);
  }

  async downloadReportFile(id: string): Promise<any> {
    return await this.http.getReportFile(id).toPromise().then((resp: HttpResponse<Blob>) => {
      const fileName = this.getFileNameFromHeaders(resp);
      this.getFileFromBlob(resp, fileName);
    });
  }

  async downloadFile(id: string): Promise<any> {
    return await this.http.getFileByName(id).toPromise().then((resp: HttpResponse<Blob>) => {
      const fileName = this.getFileNameFromHeaders(resp);
      this.getFileFromBlob(resp, fileName, true);
    });
  }

  async downloadActivityLog(title: string, startDate: string, entDate: string): Promise<any> {
    return await this.http.getActivityLogs(startDate, entDate).toPromise().then((resp: HttpResponse<Blob>) => {
      this.getFileFromBlob(resp, title);
    });
  }

  getFileFromBlob(resp: HttpResponse<Blob>, title: string, openInNewTab?: boolean) {
    const url = URL.createObjectURL(resp.body);
    if (openInNewTab) {
      window.open(url, '_blank');
    } else {
      const link = document.createElement('a');
      link.download = title;
      link.href = url;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
  }

  patchFileName(string: string): FileName {
    const patchFileName = new FileName();
    let index = string.lastIndexOf('.');
    patchFileName.name = string.substring(0, index);
    patchFileName.extension = string.substring(index++);
    return patchFileName;
  }



}

// Splits the string on every semicolon apart from the ones that are inside of quotes.
//
// It does this with a regular expresion with positive look ahead. Using a regexp is far less error prone
// than trying to write the code by looping through the string and keeping track of (four!) variables of
// mutating state.
//
// https://stackoverflow.com/questions/23582276/split-string-by-comma-but-ignore-commas-inside-quotes
//
// Regexp explanation:
//         ;               ';' The semicolon we want to split on
//         (?=             look ahead to see if there is:
//           (?:             group, but do not capture (0 or more times):
//              (?:             group, but do not capture (2 times):
//               [^"]*          any character except: '"' (0 or more times)
//               "              '"'
//              ){2}            end of grouping
//           )*              end of grouping
//           [^"]*          any character except: '"' (0 or more times)
//           $               before an optional \n, and the end of the string
//         )               end of look-ahead
//
export function splitStringOnSemicolonsOutsideOfString(str: string) {
  return str.split(/;(?=(?:(?:[^"]*"){2})*[^"]*$)/);
}
